if (!customElements.get('nuxero-bundle')) {
  customElements.define('nuxero-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency });
      this.queue = Promise.resolve();
      this.codes = null;
      this.count = Number(this.querySelector('[name="nuxero-pair-count"]:checked').value);
      this.addEventListener('change', event => {
        if (this.locked) return;
        this.count = Number(this.querySelector('[name="nuxero-pair-count"]:checked').value);
        this.refresh(event.target.closest('[data-pair]'));
        this.prepare().catch(() => {});
      });
      this.refresh();
      this.prepare().catch(() => {});
      window.addEventListener('pageshow', event => {
        if (event.persisted) { this.codes = null; this.target = undefined; this.prepare().catch(() => {}); }
      });
    }
    selection(pair) {
      const options = [...pair.querySelectorAll('[data-option]')].map(select => select.value);
      return this.variants.find(variant => variant.options.every((value, index) => value === options[index]));
    }
    items() {
      const items = new Map();
      [...this.querySelectorAll('[data-pair]')].slice(0, this.count).forEach(pair => {
        const variant = this.selection(pair);
        if (!variant?.available) throw new Error(window.variantStrings.unavailable);
        const item = items.get(variant.id) || { id: variant.id, quantity: 0 };
        item.quantity++;
        items.set(variant.id, item);
      });
      return [...items.values()];
    }
    refresh(changedPair) {
      const pairs = [...this.querySelectorAll('[data-pair]')];
      const selectors = this.querySelector('.nx-selectors');
      this.querySelectorAll('[data-tier]').forEach(tier => {
        const selected = Number(tier.dataset.tier) === this.count;
        tier.classList.toggle('nx-selected', selected);
        if (selected && selectors && selectors.parentElement !== tier) tier.append(selectors);
      });
      pairs.forEach((pair, index) => { pair.hidden = index >= this.count; });
      this.querySelectorAll('[data-tier-price]').forEach(node => {
        const count = Number(node.dataset.tierPrice);
        const price = pairs.slice(0, count).reduce((sum, pair) => sum + (this.selection(pair)?.price || 0), 0);
        node.textContent = this.money.format(Math.round(price * (count >= 2 ? .8 : 1)) / 100);
      });
      this.querySelectorAll('[data-tier-compare]').forEach(node => {
        const count = Number(node.dataset.tierCompare);
        const selected = pairs.slice(0, count).map(pair => this.selection(pair));
        const price = selected.reduce((sum, variant) => sum + (variant?.price || 0), 0) * (count >= 2 ? .8 : 1);
        const compare = selected.reduce((sum, variant) => sum + (variant?.compare_at_price || variant?.price || 0), 0);
        node.textContent = this.money.format(compare / 100);
        node.hidden = compare <= price;
        const saving = this.querySelector(`[data-tier-saving="${count}"]`);
        if (saving) { saving.textContent = `−${compare > price ? Math.round((compare - price) * 100 / compare) : 0}%`; saving.hidden = compare <= price; }
      });
      const selected = pairs.slice(0, this.count).map(pair => this.selection(pair));
      const price = Math.round(selected.reduce((sum, variant) => sum + (variant?.price || 0), 0) * (this.count >= 2 ? .8 : 1));
      const compare = selected.reduce((sum, variant) => sum + (variant?.compare_at_price || variant?.price || 0), 0);
      const amount = this.querySelector('[data-bundle-amount]');
      const compareAmount = this.querySelector('[data-bundle-compare]');
      if (amount) amount.textContent = this.money.format(price / 100);
      if (compareAmount) { compareAmount.textContent = this.money.format(compare / 100); compareAmount.hidden = compare <= price; }
      this.querySelector('.nx-bundle-note').hidden = this.count < 2;
      const productInfo = this.closest('product-info');
      productInfo?.querySelectorAll('[data-na-quick-amount]').forEach(node => { node.textContent = this.money.format(price / 100); });
      const tierLabel = this.querySelector(`[data-tier="${this.count}"] .nx-tier-label`)?.textContent;
      productInfo?.querySelectorAll('[data-na-quick-label]').forEach(node => { node.textContent = tierLabel || ''; });

      const form = this.closest('product-info')?.querySelector('product-form:not(.nx-accessory)');
      try { this.items(); form?.toggleSubmitButton?.(false); }
      catch (_) { form?.toggleSubmitButton?.(true, window.variantStrings.unavailable); }
      if (changedPair) {
        const variant = this.selection(changedPair);
        if (variant?.featured_media?.id) this.closest('product-info')?.querySelector('media-gallery')?.setActiveMedia(`${this.dataset.section}-${variant.featured_media.id}`, true);
      }
    }
    setLocked(locked) {
      this.locked = locked;
      this.querySelectorAll('input,select').forEach(input => { input.disabled = locked; });
      this.closest('product-info')?.querySelector('button[name="checkout"]')?.toggleAttribute('disabled', locked);
      this.closest('product-info')?.querySelectorAll('[data-na-submit]').forEach(button => {
        button.disabled = locked;
        button.setAttribute('aria-busy', String(locked));
      });

    }
    async request(path, data) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + path, {
        method: data ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(data ? { body: JSON.stringify(data) } : {}),
        signal: AbortSignal.timeout(15000)
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || window.cartStrings.error);
      return result;
    }
    prepare() {
      const target = this.count >= 2;
      if (this.target === target && this.pending) return this.pending;
      this.target = target;
      this.pending = this.queue.catch(() => {}).then(async () => {
        if (!this.codes) this.codes = (await this.request('cart.js')).discount_codes || [];
        const codes = this.codes.map(discount => discount.code).filter(Boolean);
        const desired = codes.filter(code => code.toUpperCase() !== 'NUXERO-VS-DUO20');
        if (target) desired.push('NUXERO-VS-DUO20');
        if ([...codes].sort().join(',') !== [...desired].sort().join(',')) {
          this.codes = (await this.request('cart/update.js', { discount: desired.join(',') })).discount_codes || [];
        }
      }).catch(error => { this.target = undefined; this.codes = null; throw error; });
      this.queue = this.pending;
      return this.pending;
    }
    populate(formData, items) {
      formData.delete('id');
      formData.delete('quantity');
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
      });
    }
  });
}
